import { PlanId, planById, readPreviewPlan } from './paywall';

/** Until org.subscriptionPlan is on the API, use Plans preview / localStorage. */
export function resolveSeatPlanId(): PlanId {
  return readPreviewPlan();
}

export const EXTRA_STAFF_SEAT_MONTHLY_USD = 49;

/** @deprecated use includedTeamSeats(planId) — kept for docs referencing "1 included" on Practice */
export const INCLUDED_STAFF_SEATS = 1;

export function includedTeamSeats(planId: PlanId): number | null {
  const plan = planById(planId);
  if (plan.includedPractitioners == null || plan.includedStaff == null) {
    return null;
  }
  return plan.includedPractitioners + plan.includedStaff;
}

export function formatPlanSeatsCopy(planId: PlanId): string {
  const plan = planById(planId);
  if (plan.includedPractitioners == null || plan.includedStaff == null) {
    return 'Unlimited practitioners & staff';
  }
  const p = plan.includedPractitioners;
  const s = plan.includedStaff;
  const pLabel = p === 1 ? 'practitioner' : 'practitioners';
  const sLabel = s === 1 ? 'staff member' : 'staff';
  return `${p} ${pLabel} & ${s} ${sLabel} included; +$${EXTRA_STAFF_SEAT_MONTHLY_USD}/mo each additional`;
}

export function formatEntitlementHint(planId: PlanId): string {
  return `${formatPlanSeatsCopy(planId)}. Each person gets their own login.`;
}

export function extraTeamSeatsAfterInvite(
  planId: PlanId,
  currentTeamCount: number,
  invitesAdding: number,
): number {
  const included = includedTeamSeats(planId);
  if (included == null) {
    return 0;
  }
  const totalAfter = currentTeamCount + invitesAdding;
  return Math.max(0, totalAfter - included);
}

export function formatStaffUsageLine(planId: PlanId, teamUsed: number): string {
  const plan = planById(planId);
  const included = includedTeamSeats(planId);

  if (included == null) {
    return `Team members: ${teamUsed} (unlimited on ${plan.name})`;
  }

  const extra = Math.max(0, teamUsed - included);
  const bucket =
    plan.includedPractitioners != null && plan.includedStaff != null
      ? `${plan.includedPractitioners} ${plan.includedPractitioners === 1 ? 'practitioner' : 'practitioners'} + ${plan.includedStaff} ${plan.includedStaff === 1 ? 'staff member' : 'staff'}`
      : `${included} included`;

  if (extra === 0) {
    return `Team: ${teamUsed} of ${included} included (${bucket})`;
  }
  return `Team: ${teamUsed} (${bucket}; +${extra} × $${EXTRA_STAFF_SEAT_MONTHLY_USD}/mo)`;
}

export function formatInviteBillingNotice(
  planId: PlanId,
  teamUsed: number,
  invitesAdding: number,
): string | null {
  const included = includedTeamSeats(planId);
  if (included == null) {
    return null;
  }

  const extra = extraTeamSeatsAfterInvite(planId, teamUsed, invitesAdding);
  if (extra <= 0) {
    return null;
  }
  const monthly = extra * EXTRA_STAFF_SEAT_MONTHLY_USD;
  if (extra === 1) {
    return `This invite exceeds your plan’s included team size (${included}). It adds 1 billable seat (+$${EXTRA_STAFF_SEAT_MONTHLY_USD}/mo). Your subscription will increase by $${monthly}/mo when billing is connected.`;
  }
  return `These invites exceed your plan’s included team size (${included}). They add ${extra} billable seats (+$${EXTRA_STAFF_SEAT_MONTHLY_USD}/mo each, $${monthly}/mo total). Your subscription will update when billing is connected.`;
}
